import { FileActionButton } from "../file-action/file-action-controls";
import React, { useState, useRef, useEffect, Suspense } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { MultiSelect } from 'primereact/multiselect';
import { Toast } from 'primereact/toast';
import AppLoading from '../app-loading/app-loading';
import routeCodeServices from '../../services/route-codes/routecode.service';
import api from '../../services/api';
import ApiConstant from '../../constants/api.constants';
import './whyNotDeliveredSummary.css';
import orderServices from '../../services/orders/orderservice';
import * as XLSX from 'xlsx';

interface RouteCodeOption {
    routeCode: string;
    routeCodeID: number;
}

interface DeliveryAttempt {
    assignedDate: string | null;
    deliveryExecutiveName: string | null;
    delayReason: string | null
}

interface WhyNotDeliveredRow {
    orderID: number;
    customerName: string;
    daysAgo: number;
    customerPhone: string;
    requestedDelivery: string | null;
    requestedBy: string;
    routeCode: string;
    deliveryAttempts: DeliveryAttempt[];
    numberOfTimesAssigned: number;
    grossAmount: number;
}

const WhyNotDeliveredSummary: React.FC = () => {
    const toast = useRef<Toast>(null);
    const [loading, setLoading] = useState(false);
    const [dataList, setDataList] = useState<WhyNotDeliveredRow[]>([]);
    const [hasSearched, setHasSearched] = useState(false);

    // Filters
    const [numberOfDays, setNumberOfDays] = useState<number | null>(null);
    const [daysOperator, setDaysOperator] = useState<string | null>(null);
    const [selectedRouteCodes, setSelectedRouteCodes] = useState<string[]>([]);
    const [routeCodeOptions, setRouteCodeOptions] = useState<RouteCodeOption[]>([]);

    // Max delivery attempt columns
    const [maxAttempts, setMaxAttempts] = useState<number>(0);

    // Load route codes on mount
    useEffect(() => {
        routeCodeServices.getRouteCodes().then((res: any) => {
            if (res && Array.isArray(res)) {
                debugger
                setRouteCodeOptions(res);
            }
        }).catch((err: any) => console.error(err));
    }, []);

    const handleFilter = async () => {
        if (!daysOperator) {
            toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please select an operator.', life: 3000 });
            return;
        }
        if (numberOfDays === null || numberOfDays === undefined) {
            toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please enter number of days.', life: 3000 });
            return;
        }
        if (numberOfDays > 30) {
            toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Number of days must be 30 or less.', life: 3000 });
            return;
        }

        setLoading(true);
        setHasSearched(true);
        try {
            const payload = {
                numberOfDays,
                daysOperator,
                routeCodes: selectedRouteCodes.length > 0 ? selectedRouteCodes : [],
            };
            const data = await orderServices.getWhyNotDeliveredSummary(payload);
            console.log('data', data);
            setDataList(data);

            // Determine max delivery attempts across all rows
            const mx = data.reduce((acc: number, row: WhyNotDeliveredRow) =>
                Math.max(acc, row.deliveryAttempts?.length ?? 0), 0);
            setMaxAttempts(mx);
        } catch (err) {
            console.error(err);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch data. Please try again.',
                life: 3000,
            });
            setDataList([]);
            setMaxAttempts(0);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setNumberOfDays(null);
        setDaysOperator(null);
        setSelectedRouteCodes([]);
        setDataList([]);
        setMaxAttempts(0);
        setHasSearched(false);
    };

    const exportExcel = () => {
        if (!dataList || dataList.length === 0) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'No data to export.',
                life: 3000,
            });
            return;
        }

        const exportData = dataList.map(row => {
            const rowData: any = {
                'Order ID': row.orderID,
                'Order Value (₹)': row.grossAmount != null ? Number(row.grossAmount).toFixed(2) : '',
                'Customer': row.customerName,
                'Days Ago': row.daysAgo,
                'Phone Number': row.customerPhone,
                'Requested Delivery': row.requestedDelivery ? new Date(row.requestedDelivery).toLocaleDateString('en-GB') : '',
                'Requested By': row.requestedBy,
                'Route Code': row.routeCode
            };

            for (let i = 0; i < maxAttempts; i++) {
                const attempt = row.deliveryAttempts?.[i];
                const attemptNum = i + 1;
                rowData[`Date ${attemptNum}`] = attempt?.assignedDate ? new Date(attempt.assignedDate).toLocaleDateString('en-GB') : '';
                rowData[`Del Ex ${attemptNum}`] = attempt?.deliveryExecutiveName || '';
                rowData[`Delay Reason ${attemptNum}`] = attempt?.delayReason || '';
            }

            return rowData;
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
        const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

        saveAsExcelFile(excelBuffer, 'Why_Not_Delivered_Summary');
    };

    const saveAsExcelFile = (buffer: any, fileName: string) => {
        import('file-saver').then((module) => {
            if (module && module.default) {
                let EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
                let EXCEL_EXTENSION = '.xlsx';
                const data = new Blob([buffer], {
                    type: EXCEL_TYPE
                });
                module.default.saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
            }
        });
    };

    // --- Column Body Templates ---
    const requestedDeliveryBody = (row: WhyNotDeliveredRow) => {
        if (!row.requestedDelivery) return <span className="wnd-empty-cell">—</span>;
        return new Date(row.requestedDelivery).toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
        });
    };

    const routeCodeBody = (row: WhyNotDeliveredRow) => (
        <span className="wnd-route-code-badge">{row.routeCode || '—'}</span>
    );

    const daysAgoBody = (row: WhyNotDeliveredRow) => {
        const days = row.daysAgo;
        const color = days > 14 ? '#dc3545' : days > 7 ? '#fd7e14' : '#28a745';
        return (
            <span style={{ fontWeight: 700, color }}>
                {days}
            </span>
        );
    };

    // Dynamic attempt date column
    const attemptDateBody = (index: number) => (row: WhyNotDeliveredRow) => {
        const attempt = row.deliveryAttempts?.[index];
        if (!attempt || !attempt.assignedDate) return <span className="wnd-empty-cell">—</span>;
        return (
            <span className="wnd-attempt-date">
                {new Date(attempt.assignedDate).toLocaleDateString('en-GB', {
                    day: '2-digit', month: 'short', year: 'numeric',
                })}
            </span>
        );
    };

    // Dynamic delivery executive column
    const attemptDeliveryExBody = (index: number) => (row: WhyNotDeliveredRow) => {
        const attempt = row.deliveryAttempts?.[index];
        if (!attempt || !attempt.deliveryExecutiveName) return <span className="wnd-empty-cell">—</span>;
        return (
            <span className="wnd-delivery-exec">
                {attempt.deliveryExecutiveName}
            </span>
        );
    };

    // Dynamic delay reason column
    const attemptDelayReasonBody = (index: number) => (row: WhyNotDeliveredRow) => {
        const attempt = row.deliveryAttempts?.[index];
        if (!attempt || !attempt.delayReason) return <span className="wnd-empty-cell">—</span>;
        return (
            <span className="wnd-delay-reason">
                {attempt.delayReason}
            </span>
        );
    };


    // Build dynamic attempt columns
    const buildAttemptColumns = () => {
        const cols: React.ReactNode[] = [];
        for (let i = 0; i < maxAttempts; i++) {
            const attemptNum = i + 1;
            cols.push(
                <Column
                    key={`date-${i}`}
                    header={<span className="wnd-attempt-header">Date {attemptNum}</span>}
                    body={attemptDateBody(i)}
                    style={{ minWidth: '120px', background: '#fffde7', borderLeft: i === 0 ? '2px solid #f1c40f' : undefined }}
                    headerStyle={{ background: '#f1c40f', color: '#333', fontWeight: 700, minWidth: '120px' }}
                />,
                <Column
                    key={`del-${i}`}
                    header={<span className="wnd-attempt-header">Del Ex {attemptNum}</span>}
                    body={attemptDeliveryExBody(i)}
                    style={{ minWidth: '130px', background: '#fffde7' }}
                    headerStyle={{ background: '#f1c40f', color: '#333', fontWeight: 700, minWidth: '130px' }}
                />,
                <Column
                    key={`delay-${i}`}
                    header={<span className="wnd-attempt-header">Delay Reason {attemptNum}</span>}
                    body={attemptDelayReasonBody(i)}
                    style={{ minWidth: '150px', background: '#fffde7' }}
                    headerStyle={{ background: '#f1c40f', color: '#333', fontWeight: 700, minWidth: '150px' }}
                />
            );
        }
        return cols;
    };

    return (
        <Suspense fallback={<AppLoading />}>
            <Toast ref={toast} position="top-right" />
            <div className="layout-content mt-3 mb-3">
                <div className="card w-full p-4">
                    {/* Header */}
                    <div className="card p-4 w-full mb-4 wnd-header-card">
                        <div className="wnd-header-gradient">
                            <div className="wnd-header-icon">
                                <i className="pi pi-truck"></i>
                            </div>
                            <div className="wnd-header-text">
                                <h5>What is required to Deliver – Summary</h5>
                                <p>Review undelivered orders by days and route code</p>
                            </div>
                            <div className="ml-auto">
                                {dataList.length > 0 && (
                                    <FileActionButton action="export"
                                        id="wnd-export-btn"
                                        className="text-black border-round"
                                        onClick={exportExcel}
                                        icon="pi pi-file-export mr-1"
                                        text
                                        raised
                                        tooltip={`Export ${dataList?.length ?? 0} record(s) to Excel`}
                                        tooltipOptions={{ position: 'top' }}
                                        style={{ width: 'auto' }}
                                    >
                                        Export
                                    </FileActionButton>
                                )}
                            </div>
                        </div>

                        {/* Filter Section */}
                        <div className="wnd-filter-label mb-2">
                            <i className="pi pi-filter" style={{ color: 'rgb(76,175,80)', fontSize: '1.05rem' }}></i>
                            <span>Filter Report</span>
                        </div>

                        <div className="wnd-filter-row">
                            {/* Days Input with Operator */}
                            <div className="wnd-filter-item">
                                <label className="wnd-filter-label-text" htmlFor="wnd-days-input">
                                    No. of Days <span className="wnd-filter-hint">(≤ 30)</span>
                                </label>
                                <div className="wnd-days-control">
                                    {/* Operator toggle buttons */}
                                    <div className="wnd-operator-group">
                                        {([['>=', '>='], ['=', '='], ['<=', '<=']] as [string, string][]).map(([op, label]) => (
                                            <button
                                                key={op}
                                                type="button"
                                                className={`wnd-op-btn${daysOperator === op ? ' wnd-op-btn--active' : ''}`}
                                                onClick={() => setDaysOperator(op)}
                                                title={op === '>' ? 'Greater than' : op === '=' ? 'Equal to' : 'Less than'}
                                            >
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                    <InputNumber
                                        id="wnd-days-input"
                                        value={numberOfDays}
                                        onValueChange={(e) => setNumberOfDays(e.value ?? null)}
                                        placeholder="Days (max 30)"
                                        min={0}
                                        max={30}
                                        inputClassName="wnd-days-input"
                                        className="wnd-input-number"
                                    />
                                </div>
                            </div>

                            {/* Route Code Multi-Select */}
                            <div className="wnd-filter-item wnd-filter-route">
                                <label className="wnd-filter-label-text" htmlFor="wnd-route-select">
                                    Route Code <span className="wnd-filter-hint">(Multiple Selection)</span>
                                </label>
                                <MultiSelect
                                    id="wnd-route-select"
                                    value={selectedRouteCodes}
                                    options={routeCodeOptions}
                                    optionLabel="name"
                                    optionValue="routeCodeID"
                                    onChange={(e) => setSelectedRouteCodes(e.value)}
                                    placeholder="Select route codes"
                                    filter
                                    display="chip"
                                    className="wnd-multiselect"
                                    showSelectAll
                                    maxSelectedLabels={3}
                                />
                            </div>

                            {/* Buttons */}
                            <div className="wnd-filter-buttons">
                                <Button
                                    id="wnd-filter-btn"
                                    label="Filter"
                                    icon="pi pi-search"
                                    className="p-button-success wnd-btn-filter"
                                    onClick={handleFilter}
                                    loading={loading}
                                    style={{ background: '#0e6f3b', border: 'none', borderRadius: '8px' }}
                                />
                                <Button
                                    id="wnd-reset-btn"
                                    label="Reset"
                                    icon="pi pi-refresh"
                                    className="wnd-btn-reset"
                                    onClick={handleReset}
                                    style={{ backgroundColor: '#547180', color: 'white', border: 'none', borderRadius: '8px' }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Data Table — shown only after filter */}
                    {hasSearched && (
                        <div className="wnd-table-section">
                            <div className="wnd-table-header mb-3">
                                <h5 className="wnd-table-title">
                                    <i className="pi pi-list mr-2"></i>
                                    Undelivered Orders
                                </h5>
                                {dataList.length > 0 && (
                                    <span className="wnd-record-count">{dataList.length} record{dataList.length !== 1 ? 's' : ''}</span>
                                )}
                            </div>

                            <div className="wnd-datatable-wrapper">
                                <DataTable
                                    value={dataList}
                                    paginator
                                    rows={10}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    stripedRows
                                    rowHover
                                    sortMode="multiple"
                                    emptyMessage="No undelivered orders found for selected filters."
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                                    loading={loading}
                                    className="wnd-datatable myorders-datatable"
                                    scrollable
                                    style={{
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        background: 'white',
                                        border: '1px solid #e0e0e0',
                                    }}
                                >
                                    {/* Fixed columns */}
                                    <Column field="orderID" header="Order ID" sortable style={{ minWidth: '100px' }} />
                                    <Column
                                        field="grossAmount"
                                        header="Order Value"
                                        sortable
                                        body={(row: WhyNotDeliveredRow) => (
                                            <span style={{ fontWeight: 600, color: '#1b5e20' }}>
                                                ₹{row.grossAmount != null ? Number(row.grossAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                                            </span>
                                        )}
                                        style={{ minWidth: '120px' }}
                                    />
                                    <Column field="customerName" header="Customer" sortable style={{ minWidth: '140px' }} />
                                    <Column field="daysAgo" header="Days Ago" sortable body={daysAgoBody} style={{ minWidth: '100px' }} />
                                    <Column field="customerPhone" header="Phone Number" sortable style={{ minWidth: '130px' }} />
                                    <Column
                                        field="requestedDelivery"
                                        header="Requested Delivery"
                                        sortable
                                        body={requestedDeliveryBody}
                                        style={{ minWidth: '150px' }}
                                    />
                                    <Column field="requestedBy" header="Updated By" sortable style={{ minWidth: '130px' }} />
                                    <Column field="numberOfTimesAssigned" header="Number of Times Assigned" sortable style={{ minWidth: '130px' }} />
                                    {/* Route Code column — highlighted */}
                                    <Column
                                        field="routeCode"
                                        header="Route Code"
                                        sortable
                                        body={routeCodeBody}
                                        style={{ minWidth: '120px', background: '#e3f2fd' }}
                                        headerStyle={{ background: '#90caf9', color: '#0d47a1', fontWeight: 700, minWidth: '120px' }}
                                    />

                                    {/* Dynamic delivery attempt columns */}
                                    {buildAttemptColumns()}
                                </DataTable>
                            </div>
                        </div>
                    )}

                    {/* Placeholder when nothing searched yet */}
                    {!hasSearched && (
                        <div className="wnd-placeholder">
                            <i className="pi pi-filter-slash wnd-placeholder-icon"></i>
                            <p className="wnd-placeholder-text">Select filters and click <strong>Filter</strong> to view undelivered orders.</p>
                        </div>
                    )}
                </div>
            </div>
            {loading && <AppLoading />}
        </Suspense>
    );
};

export default WhyNotDeliveredSummary;
